"use client";

import { SelectRadioItemType } from "@/types/ui";
import { CheckIcon, ChevronsUpDownIcon, XIcon } from "lucide-react";
import { startTransition, useEffect, useId, useState } from "react";
import { useTranslations } from "next-intl";
import { Button } from "@components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@components/ui/popover";
import { scrollCommandListOnWheel } from "@lib/ui";
import { cn } from "@lib/utils";

interface ComboboxProps {
  items?: SelectRadioItemType[];
  value?: string[];
  onValueChange?: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  isDirty?: boolean;
}

export const Combobox = ({
  items = [],
  value,
  onValueChange,
  placeholder,
  disabled,
  className,
  isDirty,
}: ComboboxProps) => {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [selectedValues, setSelectedValues] = useState<string[]>(value ?? []);
  const tCommon = useTranslations("common");

  const toggleSelection = (item: string) => {
    const data = selectedValues.includes(item)
      ? selectedValues.filter((v) => v !== item)
      : [...selectedValues, item];
    setSelectedValues(data);
    onValueChange?.(data);
  };

  const removeSelection = (item: string) => {
    const data = selectedValues.filter((v) => v !== item);
    setSelectedValues(data);
    onValueChange?.(data);
  };

  useEffect(() => {
    startTransition(() => {
      setSelectedValues(value ?? []);
    });
  }, [value]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "group h-auto min-h-9 w-full justify-between px-2.5 py-1.5",
            {
              "px-1.5": selectedValues.length > 0,
              "bg-gray-200 dark:bg-gray-700": isDirty,
            },
            className,
          )}
          disabled={disabled}
        >
          <div className="flex flex-wrap items-center gap-1.5 pr-2.5">
            {selectedValues.length > 0 ? (
              selectedValues.map((val) => {
                const item = items.find((c) => c.value === val);
                return item ? (
                  <div
                    key={val}
                    className="flex h-[calc(--spacing(5.5))] w-fit items-center justify-center gap-1 rounded-sm bg-muted px-1.5 text-xs leading-[calc(--spacing(5.5))] font-medium whitespace-nowrap text-foreground group-hover:bg-muted-foreground/25 group-hover:text-accent-foreground"
                  >
                    <span>{item.label}</span>
                    <div
                      className="opacity-50 hover:opacity-100"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeSelection(val);
                      }}
                    >
                      <XIcon className="pointer-events-none size-4" />
                    </div>
                  </div>
                ) : null;
              })
            ) : (
              // TODO placeholder
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </div>
          <ChevronsUpDownIcon className="shrink-0 text-muted-foreground/80" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-(--radix-popper-anchor-width) p-0">
        <Command>
          <CommandInput placeholder={placeholder} />
          {/* Wheel on nested scroll: keep scroll in the list inside Popover/Dialog (see tags-color). */}
          <CommandList
            className="max-h-50 overscroll-contain"
            onWheel={scrollCommandListOnWheel}
          >
            <CommandEmpty>{tCommon("words.nouns.no-data")}</CommandEmpty>
            <CommandGroup>
              {items.map((item) => (
                <CommandItem
                  key={item.value}
                  value={item.value}
                  keywords={[item.label]}
                  onSelect={() => toggleSelection(item.value)}
                >
                  <span className="truncate">{item.label}</span>
                  {selectedValues.includes(item.value) && (
                    <CheckIcon size={16} className="ml-auto" />
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
